import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  LogOut, Wrench, Search, Calendar as CalIcon, Phone, Mail, Car, Clock,
  CheckCircle2, Loader2, X,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { adminApi } from "@/lib/adminApi";
import { SERVICES } from "@/data/services";

const STATUSES = [
  { id: "pending",   label: "Pending",   color: "border-[#F5C518]/40 bg-[#F5C518]/10 text-[#F5C518]" },
  { id: "confirmed", label: "Confirmed", color: "border-sky-500/40 bg-sky-500/10 text-sky-300" },
  { id: "completed", label: "Completed", color: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  { id: "cancelled", label: "Cancelled", color: "border-red-500/40 bg-red-500/10 text-red-300" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.id, s]));
const SERVICE_MAP = Object.fromEntries(SERVICES.map((s) => [s.id, s.title]));
const HTTP_UNAUTHORIZED = 401;

export default function AdminDashboard() {
  const { admin, checking, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (dateFilter) params.date = dateFilter;
      if (statusFilter) params.status = statusFilter;
      const [{ data: list }, { data: s }] = await Promise.all([
        adminApi.get("/admin/bookings", { params }),
        adminApi.get("/admin/stats"),
      ]);
      setBookings(list);
      setStats(s);
    } catch (e) {
      if (e.response?.status === HTTP_UNAUTHORIZED) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [query, dateFilter, statusFilter, navigate]);

  useEffect(() => {
    if (admin) fetchData();
  }, [admin, fetchData]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center text-neutral-400 font-body">
        Loading…
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const { data } = await adminApi.patch(`/admin/bookings/${id}/status`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      // refresh stats
      adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      toast.success(`Booking marked ${status}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const clearFilters = () => {
    setQuery("");
    setDateFilter("");
    setStatusFilter("");
  };

  return (
    <div className="min-h-screen bg-[#121212] text-white font-body">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#121212]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-[#F5C518] flex items-center justify-center">
              <Wrench className="w-5 h-5 text-[#121212]" strokeWidth={2.5} />
            </span>
            <div>
              <div className="font-display font-extrabold uppercase tracking-tight text-base leading-none">
                <span className="text-[#F5C518]">Premium</span> Admin
              </div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">
                Booking Manager
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#F5C518]/40 hover:bg-white/[0.04] text-xs uppercase tracking-wider font-semibold text-neutral-300 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} accent />
          {STATUSES.map((s) => (
            <StatCard key={s.id} label={s.label} value={stats.by_status?.[s.id] || 0} />
          ))}
        </div>

        {/* Filters */}
        <div className="glass-strong rounded-xl p-4 mb-6 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <input
              data-testid="admin-search-input"
              type="text"
              placeholder="Search name, email, phone, vehicle…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm placeholder:text-neutral-500 focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30"
            />
          </div>
          <div className="flex gap-3">
            <input
              data-testid="admin-date-filter"
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-white focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30 [color-scheme:dark]"
            />
            <select
              data-testid="admin-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30 [color-scheme:dark]"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            {(query || dateFilter || statusFilter) && (
              <button
                onClick={clearFilters}
                data-testid="admin-clear-filters"
                className="px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-xs uppercase tracking-wider font-semibold text-neutral-300 hover:border-[#F5C518]/40 hover:text-[#F5C518] flex items-center gap-1.5"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Booking list */}
        <div data-testid="admin-bookings-list">
          {loading ? (
            <div className="py-20 flex items-center justify-center text-neutral-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading bookings…
            </div>
          ) : bookings.length === 0 ? (
            <div className="py-20 text-center text-neutral-400">
              <CheckCircle2 className="w-10 h-10 text-[#F5C518]/40 mx-auto mb-3" />
              No bookings match your filters yet.
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => (
                <BookingRow
                  key={b.id}
                  booking={b}
                  onStatus={updateStatus}
                  updating={updatingId === b.id}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent = false }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent
          ? "border-[#F5C518]/40 bg-[#F5C518]/[0.06]"
          : "border-white/8 bg-white/[0.02]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">{label}</div>
      <div className={`font-display font-extrabold text-3xl mt-1 ${accent ? "text-[#F5C518]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function BookingRow({ booking, onStatus, updating }) {
  const status = STATUS_MAP[booking.status] || STATUS_MAP.pending;
  const services = useMemo(
    () => (booking.services || []).map((id) => SERVICE_MAP[id] || id),
    [booking.services]
  );
  const created = booking.created_at
    ? typeof booking.created_at === "string"
      ? parseISO(booking.created_at)
      : new Date(booking.created_at)
    : null;
  const hasLegacyAppointment = booking.appointment_date && booking.appointment_time;
  const appointment = hasLegacyAppointment
    ? (() => {
        try {
          return format(
            parseISO(`${booking.appointment_date}T${booking.appointment_time}:00`),
            "EEE, MMM d · h:mm a"
          );
        } catch {
          return `${booking.appointment_date} ${booking.appointment_time}`;
        }
      })()
    : null;
  const hasVehicle = booking.vehicle_year || booking.vehicle_make || booking.vehicle_model;

  return (
    <div
      data-testid={`admin-booking-${booking.id}`}
      className="rounded-xl border border-white/8 bg-white/[0.02] hover:border-white/20 transition-all p-5"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* Customer */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${status.color}`}>
              {status.label}
            </span>
            {created && (
              <span className="text-[10px] text-neutral-500 uppercase tracking-wider">
                Received {format(created, "MMM d, h:mma")}
              </span>
            )}
          </div>
          <h3 className="font-display font-extrabold text-lg uppercase tracking-tight">
            {booking.customer_name}
          </h3>
          <div className="mt-2 space-y-1 text-sm text-neutral-300">
            <a href={`tel:${booking.customer_phone}`} className="flex items-center gap-2 hover:text-[#F5C518]">
              <Phone className="w-3.5 h-3.5 text-neutral-500" /> {booking.customer_phone}
            </a>
            <a href={`mailto:${booking.customer_email}`} className="flex items-center gap-2 hover:text-[#F5C518] break-all">
              <Mail className="w-3.5 h-3.5 text-neutral-500" /> {booking.customer_email}
            </a>
            {hasVehicle && (
              <div className="flex items-center gap-2">
                <Car className="w-3.5 h-3.5 text-neutral-500" />
                {[booking.vehicle_year, booking.vehicle_make, booking.vehicle_model].filter(Boolean).join(" ")}
              </div>
            )}
          </div>
        </div>

        {/* Callback preference / legacy appointment / notes */}
        <div className="lg:col-span-4 space-y-2">
          {appointment && (
            <div className="flex items-center gap-2 text-sm text-white">
              <CalIcon className="w-4 h-4 text-[#F5C518]" />
              <span className="font-semibold">{appointment}</span>
            </div>
          )}
          {booking.best_time_to_call && (
            <div className="flex items-center gap-2 text-sm text-white">
              <Clock className="w-4 h-4 text-[#F5C518]" />
              <span className="font-semibold capitalize">
                Best time: {booking.best_time_to_call}
              </span>
            </div>
          )}
          {!appointment && !booking.best_time_to_call && (
            <div className="text-sm text-neutral-400 italic">Any time works</div>
          )}
          {booking.notes && (
            <div className="text-xs text-neutral-300 px-2.5 py-1.5 rounded-md border border-[#F5C518]/30 bg-[#F5C518]/5">
              <span className="uppercase tracking-wider text-[10px] text-[#F5C518] font-bold mr-1.5">Topic:</span>
              {booking.notes}
            </div>
          )}
          {services.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {services.map((s) => (
                <span
                  key={s}
                  className="px-2 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[11px] text-neutral-300"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Status actions */}
        <div className="lg:col-span-3">
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5 block font-semibold">
            Update Status
          </label>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => {
              const active = booking.status === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => !active && onStatus(booking.id, s.id)}
                  disabled={updating || active}
                  data-testid={`admin-status-${booking.id}-${s.id}`}
                  className={`px-2.5 py-1.5 rounded-md text-[11px] uppercase tracking-wider font-semibold border transition-all ${
                    active
                      ? `${s.color} cursor-default`
                      : "border-white/10 bg-white/[0.02] text-neutral-300 hover:border-[#F5C518]/40 hover:text-white"
                  } ${updating ? "opacity-50" : ""}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
